<template>
  <div class="container-fluid">
    <div class="loading" v-if="loading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">{{ sourceid ? 'Editar catálogo' : 'Nuevo catálogo' }}</h1>
          <p class="view-header__subtitle">Configurá el catálogo y seleccioná los productos a incluir.</p>
        </div>
        <div class="view-header__actions">
          <router-link to="/ecom/catalogos" class="btn view-header__btn">
            <i class="bi bi-arrow-left"></i>
            Volver
          </router-link>
          <button type="button" class="btn view-header__btn view-header__btn--primary" :disabled="loading" @click="saveCatalog()">
            <i class="bi bi-save"></i>
            Guardar catálogo
          </button>
        </div>
      </div>
    </div>

    <!-- DETALLE DEL CATÁLOGO -->
    <div class="form-card">
      <div class="form-card__header">
        <h6 class="form-card__title">
          <i class="bi bi-info-circle"></i>
          Detalle del catálogo
        </h6>
      </div>
      <div class="form-card__body">
        <div class="row">
          <div class="col-md-4">
            <div class="form-card__field">
              <label class="form-card__label" for="cat-name">Nombre</label>
              <input
                id="cat-name"
                v-model="catalog.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': nameError }"
                placeholder="Ingresar nombre"
                @input="nameError = false"
              >
              <div v-if="nameError" class="form-card__error">El nombre es obligatorio</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-card__field">
              <label class="form-card__label" for="cat-pricelist">Lista de precios</label>
              <q-select v-model="catalog.pricelist" :options="pricesListOptions" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-card__field">
              <label class="form-card__label" for="cat-order">Ordenado por</label>
              <q-select v-model="catalog.order" :options="orderOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PRODUCTOS DEL CATÁLOGO -->
    <div class="row">
      <div class="col-12">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i class="bi bi-box-seam"></i>
              Productos del catálogo
            </h6>
            <div class="d-flex align-items-center" style="gap: 12px;">
              <span class="form-card__hint">{{ prodList.length }} producto{{ prodList.length === 1 ? '' : 's' }}</span>
              <button type="button" class="form-card__btn" @click="showFiltersModal = true">
                <i class="bi bi-funnel"></i>
                Filtros por lote
              </button>
            </div>
          </div>
          <div class="form-card__body">
            <div class="form-card__field">
              <typeahead
                :name="'productSearch'"
                :placeh="'Buscar un producto para agregar al catálogo...'"
                :clean-on="true"
                :externalsrc="'/api/products/searchProducts'"
                @choosed="productChoosen"
              />
            </div>

            <div class="data-table">
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead>
                    <tr>
                      <th>Cód. interno</th>
                      <th>Producto</th>
                      <th>Creado</th>
                      <th>Modificado</th>
                      <th class="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="prodList.length < 1">
                      <td colspan="5" class="data-table__empty">No hay productos en el catálogo</td>
                    </tr>
                    <tr v-for="prod in prodList" :key="prod.id" role="row">
                      <td class="data-table__cell-soft">{{ prod.code }}</td>
                      <td><span class="data-table__primary">{{ prod.name }}</span></td>
                      <td class="data-table__cell-soft">{{ formatDate(prod.created_at) }}</td>
                      <td class="data-table__cell-soft">{{ formatDate(prod.updated_at) }}</td>
                      <td class="text-right">
                        <button type="button" class="table-action-btn" title="Quitar del catálogo" @click="productRemove(prod.id)">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL FILTROS POR LOTE -->
    <transition v-if="showFiltersModal" name="modal">
      <div class="modal-mask-product" @click.self="showFiltersModal = false">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="form-card__header">
              <h6 class="form-card__title">
                <i class="bi bi-funnel"></i>
                Filtros por lote
              </h6>
              <button type="button" class="form-card__btn" aria-label="Cerrar" @click="showFiltersModal = false">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-card__field">
                <label class="form-card__label">Periodo de creación</label>
                <Daterange :opendir="'center'" @datepicked="dateCreated"/>
              </div>
              <div class="form-card__field">
                <label class="form-card__label">Última modificación</label>
                <Daterange :opendir="'center'" @datepicked="dateUpdated"/>
              </div>
              <div class="form-card__field">
                <label class="form-card__label">Categorías</label>
                <Treeselect
                  v-model="filters.catList"
                  class="multiList"
                  :options="categoryList"
                  :multiple="true"
                  :flat="true"
                  :open-on-click="true"
                  :append-to-body="true"
                  :z-index="1100"
                  value-consists-of="ALL_WITH_INDETERMINATE"
                />
              </div>
            </div>
            <div class="form-card__footer">
              <button type="button" class="form-card__btn" @click="showFiltersModal = false">Cancelar</button>
              <button type="button" class="form-card__btn form-card__btn--primary" @click="filterProds()">
                <i class="bi bi-search"></i>
                Agregar productos
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import Daterange from '../../global/Daterange-component.vue';
import Treeselect from '@riophae/vue-treeselect'
import typeahead from '../../global/Typeahead.vue';
import QSelect from '@/components/shared/QSelect.vue';
import moment from 'moment';

export default {
  props: ['sourceid'],
  components: {
      Daterange,
      Treeselect,
      typeahead,
      QSelect
    },
  data() {
    return {
        config:{
            endpoint :'/api/catalogs/add',
            sourceurl :'/ecom/catalogos'
        },
        catalog:{
            name:'',
            pricelist:1,
            order:'abc',
            products:[],
        },
        filters:{
            catList:[],
            createdFromDate:null,
            createdToDate:null,
            updatedFromDate:null,
            updatedToDate:null,
        },
        categoryList:[],
        pricesList:[],
        prodList:[],
        title:'',
        loading:true,
        nameError:false,
        showFiltersModal:false,
        orderOptions: [
            { value: 'abc', label: 'Alfabético' },
            { value: 'old', label: 'Creados más antiguamente' },
            { value: 'new', label: 'Creados más recientemente' },
            { value: 'upd', label: 'Modificados recientemente' },
        ],
    }
  },
  computed: {
    pricesListOptions() {
        return this.pricesList.map(price => ({ value: price.id, label: price.name }));
    },
  },
  mounted() {
    this.loadCategories();
    this.loadPriceLists();

    if(this.sourceid!=null){
        this.title="Editar catálogo";
        this.config.endpoint= '/api/catalogs/edit/' + this.sourceid;
        this.setForEdit(this.sourceid);
    }else{
        this.title="Nuevo catálogo";
        this.loading=false;
    }
  },
  methods: {
    loadCategories(){
        let vm= this;
        this.$http.get("/api/categories/getCategoryTree")
        .then((response) => {
            if (response.status==200) {
                vm.categoryList = response.data.data;
            } else {
              // no-op intencional
                
            }
        }).catch((error) => {
            console.log('error: '+error);
        })
    },
    loadPriceLists(){
        let vm= this;
        this.$http.get("/api/priceslist/getPriceList")
        .then((response) => {
            if (response.status==200) {
                vm.pricesList = response.data.data;
            } else {
              // no-op intencional
                
            }
        }).catch((error) => {
            console.log('error: '+error);
        })
    },
    setForEdit(id) {
          let vm= this;
          this.$http.get("/api/catalogs/getCatalog/"+id)
          .then((response) => {
            if (response.status==200) {
                let result = response.data.data;
                console.log('result',result);
                vm.catalog.name = result.name;
                vm.catalog.pricelist = result.pricelist;
                vm.catalog.order = result.order;
                vm.prodList = result.products;
            } else {
              // no-op intencional

            }
          }).catch((error) => {
            console.log('error: '+error);
          }).finally(() => {
            vm.loading = false;
          })
    },
    formatDate(date){
        if(date!=null){
            return moment(String(date)).format('DD/MM/YYYY');
        }else{
            return '';
        }
    },
    dateCreated: function (date) {
        this.filters.createdFromDate = date.startDate;
        this.filters.createdToDate=date.endDate;
    },
    dateUpdated: function (date) {
        this.filters.updatedFromDate = date.startDate;
        this.filters.updatedToDate=date.endDate;
    },
    filterProds() {
        let vm = this;
        let outdata= vm.filters;
        outdata.order = vm.catalog.order;
        this.$http.post("/api/catalogs/filterProds", outdata)
        .then((response) => {
            if (response.status==200) {
                vm.prodList = response.data.data;
                vm.showFiltersModal = false;
            } else {
              // no-op intencional

            }
        }).catch((error) => {
            console.log('error: '+error);
        })
    },
    cleanProds(){
        this.prodList=[];
        this.catalog.products=[];
    },
    productChoosen(a){
        var data = JSON.parse(JSON.stringify(a));
        var isInArray = this.catalog.products.findIndex(function(i){ return i === data.id }) !== -1;
        if(!isInArray){
            this.prodList.push(data);
        }
    },
    productRemove(id) {
        this.prodList.splice(this.prodList.findIndex(function(i){return i.id === id;}), 1);
    },
    saveCatalog(){
        if (this.loading) return;
        let vm=this;
        if(this.catalog.name==''){
            this.nameError = true;
            alert('Es necesario ingresar un nombre');
            return false;
        }
        this.nameError = false;
        if(this.catalog.products.length<1){
            alert('El catalogo no tiene productos asignados');
            return false;
        }

        this.loading = true;
        this.$http.post(vm.config.endpoint, vm.catalog)
        .then((response) => {
            if (response.data.code ==201) {

                window.location.href = vm.config.sourceurl;
            } else {
              // no-op intencional

            }
        }).catch((error) => {
            console.log('error: '+error);
        }).finally(() => {
            vm.loading = false;
        })

    }
  },
  watch: {
	'prodList':{handler: function () {
        let aux=[];
        this.prodList.forEach(function(prod) {
            aux.push({'id':prod.id,'priority':0});
        });
        this.catalog.products=aux;
    }}
  },
}
</script>

<style scoped>
.modal-mask-product {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 5vh 24px 24px;
  overflow-y: auto;
}

.modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 720px;
}

.modal-content {
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
  background: var(--surface);
  /* overflow visible para que dropdowns (daterange, treeselect) escapen del modal */
  overflow: visible;
}

/* Como modal-content tiene overflow visible, los corners no clippean los bg
   de header/footer: aplicamos border-radius directamente sobre ellos. */
.modal-content >>> .form-card__header {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.modal-content >>> .form-card__footer {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.modal-body {
  padding: 20px 24px;
  /* overflow visible para no clippear popups internos */
  overflow: visible;
}

.modal-enter,
.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-content,
.modal-leave-active .modal-content {
  transform: scale(1.02);
}
</style>
